import { useEffect, useMemo, useState } from 'react';
import {
  FiGift,
  FiEdit2,
  FiFilter,
  FiHash,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiLock,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  TableActions,
  BenefitForm,
  MultiSelect,
} from '../../components';
import '../CompanyManager/style.css';

export default function BenefitManager() {
  const permissions = usePermission('management');
  const [benefits, setBenefits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [benefitToDelete, setBenefitToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (permissions.canView) {
      loadInitialData();
    }
  }, [permissions.canView]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, companyFilter]);

  async function loadInitialData() {
    try {
      setLoading(true);
      const { data: companiesData } = await Api.get('/companies');
      const companyList = companiesData || [];
      setCompanies(companyList);
      setCompanyFilter('all');
      await loadBenefits('all', companyList);
    } catch (err) {
      setError('Erro ao carregar dados iniciais.');
      setLoading(false);
    }
  }

  // Carregar benefícios
  async function loadBenefits(filterValue = companyFilter, companiesList = companies) {
    if (!permissions.canView) return;

    try {
      setLoading(true);
      let data = [];

      if (filterValue === 'all') {
        const promises = companiesList.map(comp => 
          Api.getBenefits(comp.id).then(res => 
            Array.isArray(res) ? res.map(b => ({ ...b, company: comp, company_id: comp.id })) : []
          ).catch(() => [])
        );
        const results = await Promise.all(promises);
        data = results.flat();
      } else {
        const res = await Api.getBenefits(filterValue);
        if (Array.isArray(res)) {
          const company = companiesList.find(c => String(c.id) === String(filterValue));
          data = res.map(b => ({ ...b, company, company_id: filterValue }));
        }
      }

      if (Array.isArray(data)) {
        setBenefits(data);
      } else {
        setError('Não foi possível carregar os benefícios.');
      }
    } catch (err) {
      setError('Erro ao carregar benefícios. Tente novamente.');
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }

  function handleCreate() {
    setSelectedBenefit(null);
    setIsModalOpen(true);
  }

  function handleEdit(benefit) {
    setSelectedBenefit(benefit);
    setIsModalOpen(true);
  }

  function handleFormSuccess() {
    loadBenefits();
    setIsModalOpen(false);
    setSelectedBenefit(null);
  }

  function handleRequestDelete(benefit) {
    setBenefitToDelete(benefit);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!benefitToDelete) return;

    // Determine company ID: benefit's company_id or filter value
    const companyId = benefitToDelete.company_id || benefitToDelete.company?.id || (companyFilter !== 'all' ? companyFilter : null);

    if (!companyId) {
      alert('Erro ao identificar a empresa do benefício.');
      return;
    }

    try {
      setIsDeleting(true);
      const result = await Api.deleteBenefit(companyId, benefitToDelete.id);

      if (result && !result.error) {
        setBenefits(prev => prev.filter(b => b.id !== benefitToDelete.id));
        setIsDeleteModalOpen(false);
        setBenefitToDelete(null);
      } else {
        alert(
          result && result.error
            ? result.error
            : 'Erro ao excluir benefício.',
        );
      }
    } catch (err) {
      alert('Erro ao excluir benefício.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleCancelDelete() {
    if (isDeleting) return;

    setIsDeleteModalOpen(false);
    setBenefitToDelete(null);
  }

  const companyOptions = useMemo(() => {
    const options = companies.map(c => ({
      value: String(c.id),
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
    }));
    return [{ value: 'all', label: 'TODAS' }, ...options];
  }, [companies]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      const id = idsWithoutAll[0];
      setCompanyFilter(id);
      loadBenefits(id);
    } else {
      setCompanyFilter('all');
      loadBenefits('all');
    }
  }

  const filteredBenefits = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return benefits.filter(benefit => {
      const matchesSearch =
        !term ||
        (benefit.name || '').toLowerCase().includes(term) ||
        (benefit.description || '').toLowerCase().includes(term) ||
        (benefit.value != null ? String(benefit.value) : '').toLowerCase().includes(term) ||
        (benefit.company?.trade_name || '').toLowerCase().includes(term);

      const companyId = benefit.company_id || benefit.company?.id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesCompany;
    });
  }, [benefits, searchTerm, companyFilterValues]);

  const totalItems = filteredBenefits.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedBenefits = filteredBenefits.slice(
    startIndex,
    startIndex + pageSize,
  );

  const columns = [
    {
      id: 'benefit',
      header: 'Benefício',
      width: '2.2fr',
      cellClassName: 'manager-table-cell-primary',
      render: benefit => (
        <>
          <div className="manager-table-avatar">
            <FiGift size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">
              {benefit.name}
            </span>
            <span className="manager-table-subtitle">
              ID: {benefit.id}
            </span>
          </div>
        </>
      ),
    },
    {
      id: 'description',
      header: 'Descrição',
      width: '2fr',
      cellClassName: 'manager-table-cell',
      render: benefit => (
        <span className="manager-table-subtitle">
          {benefit.description || 'Sem descrição'}
        </span>
      ),
    },
    {
      id: 'value',
      header: 'Valor',
      width: '1.4fr',
      cellClassName: 'manager-table-cell manager-table-col-cnpj',
      render: benefit => {
        const raw = benefit.value != null ? String(benefit.value) : '';
        const formatted = raw
          ? `R$ ${Number(raw).toFixed(2).replace('.', ',')}`
          : 'R$ 0,00';

        return (
          <div className="manager-table-cnpj">
            <span>{formatted}</span>
          </div>
        );
      },
    },
    {
      id: 'frequency',
      header: 'Frequência',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: benefit => (
        <span className="manager-table-text">
          {benefit.frequency === 'daily' ? 'Diário' : 'Mensal'}
        </span>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: benefit => {
        const companyName = benefit.company ? (benefit.company.corporate_name || benefit.company.trade_name) : 'N/A';
        return <span className="manager-table-text" style={{ fontWeight: 'normal' }}>{companyName}</span>;
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: benefit => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(benefit);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleRequestDelete(benefit);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  function BenefitManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && (
          <BenefitManagerSkeletonWrapper />
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiGift size={22} />
                </div>
                <div>
                  <h1>Benefícios</h1>
                  <p>
                    Visualize, filtre e gerencie todos os benefícios da empresa selecionada.
                  </p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button onClick={handleCreate} className="manager-header-button">
                  <FiPlus size={18} />
                  Novo Benefício
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {benefits.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredBenefits.length} de {benefits.length} benefícios
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="company-filter"
                    options={companyOptions}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="benefit-search"
                      placeholder="Busque por nome, descrição ou valor"
                      value={searchTerm}
                      onChange={event => setSearchTerm(event.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {error && <div className="error">{error}</div>}

            {!loading && !error && benefits.length === 0 && (
              <div className="empty-state">
                <p>Nenhum benefício cadastrado.</p>
              </div>
            )}

            {!loading && !error && benefits.length > 0 && (
              <Table
                items={paginatedBenefits}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={benefit => benefit.id}
                emptyMessage="Nenhum benefício encontrado com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedBenefit ? 'Editar Benefício' : 'Novo Benefício'}
        closeOnOverlayClick={false}
      >
        <BenefitForm
          companyId={companyFilter === 'all' ? null : companyFilter}
          companies={companies}
          initialData={selectedBenefit}
          onSuccess={handleFormSuccess}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir o benefício{' '}
            {benefitToDelete && (
              <span className="delete-modal-company-name">
                {benefitToDelete.name}
              </span>
            )}
            ?
          </p>
          <p>
            Essa ação não poderá ser desfeita.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}

